import React, { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import {
  type BlockQuery as Query,
  type BlockQueryFilter,
  type BlockQuerySource,
  useGetReportingLensBlockQuery,
} from '~/hooks/apis/reporting/useGetReportingLensBlockQuery';

import LensSqlDialog from '../cards/LensSqlDialog';

import { dataUpTo } from './LensBoard';
import { rangeSummary } from './RangePick';
import type { Envelope, LensDocumentData, LensState } from './types';

type BlockQueryProps = {
  lensId: string;
  lensVersion: number;
  block: { id: string; title: string };
  state: LensState;
  data?: LensDocumentData;
  sql: boolean;
  onShowSql: () => void;
  compact?: boolean;
};

const BIGQUERY_CONSOLE = 'https://console.cloud.google.com/bigquery';

const ORIGIN_WORDS: Record<BlockQueryFilter['origin'], string> = {
  filter: 'board filter',
  select: 'picked on the board',
  query: 'part of this block',
};

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ borderTop: 1, borderColor: 'divider', fontSize: 13, fontWeight: 600, pt: 1.5 }}>
      {children}
    </Typography>
  );
}

function Code({ children, label }: { children: string; label: string }) {
  return (
    <Box
      aria-label={label}
      component="pre"
      sx={{ bgcolor: 'action.hover', borderRadius: '8px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.5, m: 0, maxHeight: 360, overflow: 'auto', p: 1.5, whiteSpace: 'pre' }}
      tabIndex={0}
    >
      {children}
    </Box>
  );
}

function sourceText(source: BlockQuerySource): string {
  if (source.kind === 'catalogue') return `Governed dataset ${source.catalogue}${source.version ? `, version ${source.version}` : ''}`;
  if (source.kind === 'sql') return 'SQL written for this Lens';
  return `A table kept in this Lens, ${source.rows.toLocaleString('en-AU')} rows`;
}

function bytesText(bytes: number): string {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  const power = bytes > 0 ? Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024))) : 0;
  return `${(bytes / 1024 ** power).toLocaleString('en-AU', { maximumFractionDigits: power ? 1 : 0 })} ${units[power]}`;
}

function readText(envelopes: Array<Envelope>): string | undefined {
  if (!envelopes.length) return undefined;
  if (envelopes.every((envelope) => envelope.cached)) return 'Answered from BigQuery’s cache';
  const bytes = envelopes.reduce((sum, envelope) => sum + (envelope.bytes ?? 0), 0);
  return bytes ? `Read ${bytesText(bytes)} in BigQuery` : undefined;
}

function filterText(filter: BlockQueryFilter): string {
  if (filter.range) return `${filter.label}: ${rangeSummary(filter.range)}`;
  return `${filter.label}: ${filter.exclude ? 'not ' : ''}${filter.values.join(', ')}`;
}

function CompiledSql({ sql, withheld, inline = false }: { sql?: string; withheld?: string; inline?: boolean }) {
  const { enqueueSnackbar } = useSnackbar();
  const [showDetails, setShowDetails] = useState(false);
  const copy = async (then?: () => void) => {
    try {
      await navigator.clipboard.writeText(sql ?? '');
      enqueueSnackbar(then ? 'SQL copied. Paste it into a new BigQuery query.' : 'SQL copied.', { variant: 'success' });
      then?.();
    } catch {
      enqueueSnackbar('The SQL didn’t copy. Select it and copy it instead.', { variant: 'warning' });
    }
  };
  return sql ? (
    <Stack data-lens-sql spacing={1}>
      {inline && <Code label="Compiled SQL">{sql}</Code>}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button onClick={() => setShowDetails(true)} size="small" variant="outlined">View SQL &amp; cost</Button>
        <Button onClick={() => void copy()} size="small" startIcon={<ContentCopyOutlinedIcon fontSize="small" />} variant="text">
          Copy SQL
        </Button>
        <Tooltip title="Copy SQL and open BigQuery">
          <IconButton aria-label="Copy SQL and open BigQuery" onClick={() => void copy(() => window.open(BIGQUERY_CONSOLE, '_blank', 'noopener'))} size="small" sx={{ borderRadius: '8px', color: 'primary.main' }}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <LensSqlDialog open={showDetails} onClose={() => setShowDetails(false)} title="Query SQL & scan cost" sql={sql} executed />
    </Stack>
  ) : withheld ? (
    <Typography color="text.secondary" variant="body2">
      {withheld}
    </Typography>
  ) : null;
}

export function BlockSql({ lensId, lensVersion, blockId, state }: { lensId: string; lensVersion: number; blockId: string; state: LensState }) {
  const lineage = useGetReportingLensBlockQuery(lensId, lensVersion, blockId, state);
  const queries = lineage.data?.queries ?? [];
  if (lineage.isLoading) return <Box aria-busy sx={{ bgcolor: 'action.hover', borderRadius: '8px', height: 120 }} />;
  if (lineage.error)
    return (
      <Typography color="error" role="alert" variant="body2">
        {lineage.error.message || 'The query couldn’t be read. Try again.'}
      </Typography>
    );
  return (
    <Stack spacing={1.5}>
      {queries.map((query) => (
        <Stack key={query.id} spacing={0.5}>
          {queries.length > 1 ? <Typography sx={{ fontWeight: 600 }} variant="body2">{query.measures.map((measure) => measure.label).join(', ')}</Typography> : null}
          <CompiledSql inline sql={query.sql} withheld={lineage.data?.sqlWithheld} />
        </Stack>
      ))}
    </Stack>
  );
}

function QuerySection({ query, many, sql, withheld, compact = false }: { query: Query; many: boolean; sql: boolean; withheld?: string; compact?: boolean }) {
  return (
    <Stack data-lens-block-query={query.id} spacing={1.5}>
      {many ? <Typography sx={{ fontWeight: 600 }} variant="body2">{query.measures.map((measure) => measure.label).join(', ')}</Typography> : null}
      {!compact && <Box>
        <Typography variant="body2">{query.source.label}</Typography>
        <Typography color="text.secondary" variant="caption">
          {sourceText(query.source)}
        </Typography>
      </Box>}
      {!compact && sql && query.source.kind === 'sql' && query.source.sql ? <Code label={`SQL of ${query.source.label}`}>{query.source.sql}</Code> : null}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {query.measures.map((measure) => (
          <Box component="li" key={measure.id}>
            <Stack alignItems="center" direction="row" spacing={0.5}>
              <Typography variant="body2">{measure.label}</Typography>
              {measure.certified ? (
                <Tooltip title="Certified measure">
                  <VerifiedOutlinedIcon aria-label="Certified" color="primary" sx={{ fontSize: 14 }} />
                </Tooltip>
              ) : null}
            </Stack>
            <Typography color="text.secondary" component="p" variant="caption">
              {compact ? undefined : measure.definition ?? (measure.certified ? 'Certified in the governed catalogue.' : 'Defined in this Lens.')}
            </Typography>
            {sql && measure.sql ? (
              <Typography component="code" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, overflowWrap: 'anywhere' }}>
                {measure.sql}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Stack>
      {(!compact || query.period.comparisons.length > 0 || query.by.length > 0) && <Typography color="text.secondary" sx={{ fontSize: 12 }}>
        {query.period.current}
        {query.period.comparisons.length ? <Box component="span" sx={{ color: 'text.secondary' }}>{` vs ${query.period.comparisons.join(' and ')}`}</Box> : null}
        {query.by.length ? <Box component="span" sx={{ color: 'text.secondary' }}>{` · by ${query.by.join(', ').toLowerCase()}`}</Box> : null}
      </Typography>}
      {query.filters.length ? (
        <Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {query.filters.map((filter) => (
            <Typography component="li" key={`${filter.origin}:${filter.column}`} variant="body2">
              {filterText(filter)}
              <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${ORIGIN_WORDS[filter.origin]}`}</Box>
            </Typography>
          ))}
        </Stack>
      ) : null}
      {sql ? <CompiledSql sql={query.sql} withheld={withheld} /> : null}
    </Stack>
  );
}

export default function BlockQuery({ lensId, lensVersion, block, state, data, sql, onShowSql, compact = false }: BlockQueryProps) {
  const lineage = useGetReportingLensBlockQuery(lensId, lensVersion, block.id, state);
  const planned = data?.blocks[block.id];
  const envelopes = (planned?.queries ?? (planned?.query ? [planned.query] : [])).map((id) => data?.queries[id]).filter((entry): entry is Envelope => Boolean(entry));
  const upTo = dataUpTo(envelopes);
  const read = readText(envelopes);
  const queries = lineage.data?.queries ?? [];
  const region = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (sql) (region.current?.querySelector('[data-lens-sql]') ?? region.current)?.scrollIntoView({ block: 'nearest' });
  }, [sql, queries.length]);

  return (
    <Stack aria-label={`How ${block.title} is calculated`} data-lens-query-panel={block.id} ref={region} role="region" spacing={2}>
      {upTo || read ? (
        <Typography color="text.secondary" sx={{ bgcolor: 'action.hover', borderRadius: '8px', px: 1.5, py: 1, fontSize: 12 }}>
          {[upTo ? `Data up to ${upTo}` : undefined, read].filter(Boolean).join(' · ')}
        </Typography>
      ) : null}
      {lineage.isLoading ? <Box aria-busy sx={{ bgcolor: 'action.hover', borderRadius: '8px', height: 160 }} /> : null}
      {lineage.error ? (
        <Typography color="error" role="alert" variant="body2">
          {lineage.error.message || 'The query couldn’t be read. Try again.'}
        </Typography>
      ) : null}
      {lineage.data?.notApplied.length ? (
        <Typography color="text.secondary" variant="body2">
          {`Not filtered by ${lineage.data.notApplied.map((control) => control.label).join(', ')}: this block’s data has no such field.`}
        </Typography>
      ) : null}
      {queries.map((query, index) => (
        <React.Fragment key={query.id}>
          {index === 0 ? (!compact && <Heading>{queries.length > 1 ? 'Queries' : 'Source and measures'}</Heading>) : <Box sx={{ borderTop: 1, borderColor: 'divider' }} />}
          <QuerySection compact={compact} many={queries.length > 1} query={query} sql={sql} withheld={lineage.data?.sqlWithheld} />
        </React.Fragment>
      ))}
      {queries.length && !sql ? (
        <Box>
          <Button onClick={onShowSql} size="small" variant="text">
            Show SQL
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
}
